/**
 * ware-cart-drawer
 * Slide-in cart drawer from right. Opens on cart:open and cart:item-added events.
 * Includes free shipping progress bar, line items, qty stepper, recommendations.
 * Renders subscription details when item.selling_plan_allocation is present.
 */
class WareCartDrawer extends HTMLElement {
  connectedCallback() {
    this._backdrop = document.querySelector('.ware-cart-drawer-backdrop');
    this._drawer = this;
    this._closeBtn = this.querySelector('[data-cart-close]');
    this._body = this.querySelector('.ware-cart-drawer__body');
    this._totalEl = this.querySelector('[data-cart-total]');
    this._shippingFill = this.querySelector('.ware-cart-drawer__shipping-fill');
    this._shippingText = this.querySelector('[data-shipping-text]');
    this._checkoutBtn = this.querySelector('[data-checkout-btn]');
    this._recsSection = this.querySelector('[data-recommendations]');
    this._recGrid = this.querySelector('[data-rec-grid]');

    this._upsellProducts = this._loadUpsellProducts();

    this._injectStyles();

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:item-added', () => {
      this.open();
      this.refresh();
    });

    if (this._closeBtn) {
      this._closeBtn.addEventListener('click', () => this.close());
    }
    if (this._backdrop) {
      this._backdrop.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  _loadUpsellProducts() {
    try {
      const el = document.getElementById('ware-cart-upsell-data');
      if (!el) return [];
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  _injectStyles() {
    if (document.getElementById('ware-cart-drawer-sub-styles')) return;
    const style = document.createElement('style');
    style.id = 'ware-cart-drawer-sub-styles';
    style.textContent = `
      .ware-cart-item__plan-badge {
        display: inline-block;
        background: #1f5a36;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 7px;
        border-radius: 4px;
        line-height: 1;
        margin: 4px 0 4px;
      }
      .ware-cart-item__plan-name {
        display: block;
        font-size: 12px;
        color: #555;
        margin: 0 0 4px;
        line-height: 1.35;
      }
      .ware-cart-item__price s {
        color: #9a9a9a;
        font-weight: 400;
        margin-right: 6px;
        font-size: 0.92em;
      }
      /* Upsell section: contain scroll so it never pans the whole drawer */
      .ware-cart-drawer__recommendations {
        overflow: hidden;
      }
      .ware-cart-drawer__rec-grid {
        display: flex;
        flex-direction: column;
        overflow-x: hidden;
        overflow-y: visible;
        overscroll-behavior: none;
        touch-action: pan-y;
      }
      .ware-cart-upsell-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 0;
        border-top: 1px solid rgba(26,20,12,0.1);
        touch-action: pan-y;
      }
      .ware-cart-upsell-card:first-child {
        border-top: none;
      }
      .ware-cart-upsell-card__img {
        width: 56px;
        height: 56px;
        object-fit: cover;
        border-radius: 4px;
        flex: 0 0 56px;
        background: #ece5d3;
      }
      .ware-cart-upsell-card__info {
        flex: 1 1 auto;
        min-width: 0;
      }
      .ware-cart-upsell-card__title {
        font-size: 13px;
        font-weight: 600;
        margin: 0 0 2px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .ware-cart-upsell-card__price {
        font-size: 12px;
        color: #4a4a4a;
      }
      .ware-cart-upsell-card__btn {
        flex: 0 0 auto;
        padding: 8px 14px;
        font-size: 12px;
        font-family: 'IBM Plex Mono', monospace;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        background: #1a140c;
        color: #f6f1e7;
        border: none;
        border-radius: 3px;
        cursor: pointer;
        transition: opacity 0.15s ease;
        white-space: nowrap;
      }
      .ware-cart-upsell-card__btn:hover { opacity: 0.85; }
      .ware-cart-upsell-card__btn:disabled { opacity: 0.5; cursor: default; }
    `;
    document.head.appendChild(style);
  }

  open() {
    this.classList.add('is-open');
    if (this._backdrop) this._backdrop.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.setAttribute('aria-hidden', 'false');
    const closeBtn = this.querySelector('[data-cart-close]');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    this.refresh();
  }

  close() {
    this.classList.remove('is-open');
    if (this._backdrop) this._backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
    this.setAttribute('aria-hidden', 'true');
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this._renderCart(cart);
    } catch (e) {
      console.error('WARE cart refresh failed', e);
    }
  }

  _renderCart(cart) {
    this._updateShippingBar(cart.total_price);
    this._updateTotal(cart.total_price);
    this._renderItems(cart.items);
    this._renderRecommendations(cart.items);
  }

  _updateShippingBar(totalCents) {
    if (!this._shippingFill || !this._shippingText) return;
    const threshold = (window.wareTheme && window.wareTheme.freeShippingThreshold) ? window.wareTheme.freeShippingThreshold * 100 : 2500;
    const pct = Math.min((totalCents / threshold) * 100, 100);
    this._shippingFill.style.width = pct + '%';

    if (totalCents >= threshold) {
      this._shippingText.textContent = 'Free delivery unlocked';
    } else {
      const remaining = ((threshold - totalCents) / 100).toFixed(2);
      this._shippingText.textContent = `£${remaining} away from free delivery`;
    }
  }

  _updateTotal(totalCents) {
    if (!this._totalEl) return;
    const formatted = (totalCents / 100).toFixed(2);
    this._totalEl.textContent = `£${formatted}`;
    if (this._checkoutBtn) {
      this._checkoutBtn.textContent = `Checkout · £${formatted}`;
    }
  }

  _escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  _renderItems(items) {
    if (!this._body) return;
    const itemsContainer = this._body.querySelector('[data-cart-items]');
    const emptyState = this._body.querySelector('[data-cart-empty]');

    if (!itemsContainer) return;

    if (items.length === 0) {
      itemsContainer.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      return;
    }

    if (emptyState) emptyState.hidden = true;

    itemsContainer.innerHTML = items.map(item => {
      const isSub = !!item.selling_plan_allocation;
      const planName = isSub && item.selling_plan_allocation.selling_plan
        ? this._escape(item.selling_plan_allocation.selling_plan.name)
        : '';
      const hasDiscount = item.original_line_price !== item.final_line_price;
      const originalLinePrice = (item.original_line_price / 100).toFixed(2);
      const finalLinePrice = (item.final_line_price / 100).toFixed(2);

      return `
      <div class="ware-cart-item" data-key="${this._escape(item.key)}">
        <div class="ware-cart-item__image">
          ${item.image ? `<img src="${this._escape(item.image)}" alt="${this._escape(item.title)}" width="80" height="80" loading="lazy">` : ''}
        </div>
        <div class="ware-cart-item__info">
          <div class="ware-cart-item__name">${this._escape(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="ware-cart-item__variant">${this._escape(item.variant_title)}</div>` : ''}
          ${isSub ? `<span class="ware-cart-item__plan-badge">Subscribe &amp; save</span>` : ''}
          ${isSub && planName ? `<span class="ware-cart-item__plan-name">${planName}</span>` : ''}
          <div class="ware-cart-item__price">
            ${hasDiscount ? `<s>£${originalLinePrice}</s>` : ''}
            <span>£${finalLinePrice}</span>
          </div>
          <div class="ware-cart-item__actions">
            <div class="ware-qty-stepper">
              <button class="ware-qty-stepper__btn" data-change="-1" data-key="${this._escape(item.key)}" aria-label="Decrease quantity">−</button>
              <span class="ware-qty-stepper__value">${item.quantity}</span>
              <button class="ware-qty-stepper__btn" data-change="1" data-key="${this._escape(item.key)}" aria-label="Increase quantity">+</button>
            </div>
            <button class="ware-cart-item__remove" data-remove="${this._escape(item.key)}">Remove</button>
          </div>
        </div>
      </div>
    `;
    }).join('');

    itemsContainer.querySelectorAll('[data-change]').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const change = parseInt(btn.dataset.change);
        const item = items.find(i => i.key === key);
        if (!item) return;
        this._updateQty(key, item.quantity + change);
      });
    });

    itemsContainer.querySelectorAll('[data-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        this._updateQty(btn.dataset.remove, 0);
      });
    });
  }

  _renderRecommendations(cartItems) {
    if (!this._recsSection || !this._recGrid || !this._upsellProducts.length) return;

    // Filter out products already in the cart
    const cartProductIds = cartItems.map(i => i.product_id);
    const recs = this._upsellProducts.filter(p => !cartProductIds.includes(p.productId) && p.available);

    if (recs.length === 0) {
      this._recsSection.hidden = true;
      return;
    }

    this._recsSection.hidden = false;
    this._recGrid.innerHTML = recs.map(p => `
      <div class="ware-cart-upsell-card">
        ${p.image ? `<img class="ware-cart-upsell-card__img" src="${this._escape(p.image)}" alt="${this._escape(p.title)}" width="56" height="56" loading="lazy">` : '<div class="ware-cart-upsell-card__img"></div>'}
        <div class="ware-cart-upsell-card__info">
          <div class="ware-cart-upsell-card__title">${this._escape(p.title)}</div>
          <div class="ware-cart-upsell-card__price">${this._escape(p.price)}</div>
        </div>
        <button
          class="ware-cart-upsell-card__btn"
          data-upsell-add
          data-variant-id="${p.variantId}"
          type="button"
        >Add</button>
      </div>
    `).join('');

    this._recGrid.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        const originalText = btn.textContent;
        btn.textContent = 'Adding...';
        btn.disabled = true;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
          });
          if (res.ok) {
            btn.textContent = 'Added ✓';
            this.refresh();
          } else {
            btn.textContent = originalText;
            btn.disabled = false;
          }
        } catch (e) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
      });
    });
  }

  async _updateQty(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) })
      });
      const cart = await res.json();
      this._renderCart(cart);
    } catch (e) {
      console.error('WARE cart update failed', e);
    }
  }
}

customElements.define('ware-cart-drawer', WareCartDrawer);
